import {Component}                                                         from '@angular/core';
import {FormArray, UntypedFormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MatSnackBar}                                                       from '@angular/material/snack-bar';
import {ActivatedRoute, Router}                                            from '@angular/router';
import {AddressType}                                                       from '@src/app/admin/settings/models/addresses.model';
import {OrganisationService}                                               from '@src/app/admin/settings/services/organisation.service';
import {User}                                                              from '@src/app/admin/users/models/user.model';
import {UsersService}                                                      from '@src/app/admin/users/services/users.service';
import {AuthService}                                                       from '@src/app/core/services/auth.service';
import {SettingsService}                                                   from '@src/app/core/services/settings.service';
import {ApiService}                                                        from '@src/app/shared/services/api.service';
import {environment}                                                       from '@src/environments/environment';
import {firstValueFrom}                                                    from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-organisation-initial-login',
    templateUrl: './organisation-initial-login.component.html',
    styleUrls: ['./organisation-initial-login.component.scss']
})
export class OrganisationInitialLoginComponent
{
    public title = '';
    public organisationName = '';
    public organisationForm;
    public submitted: boolean = false;
    public systemUsers: User;
    public imagePreview: any = null;
    private previousBillingAddress: any = null;

    public get f()
    {
        return this.organisationForm.controls;
    }

    public get system_owners()
    {
        return this.f['system_owners'] as FormArray;
    }

    public constructor(
        private authService: AuthService,
        public router: Router,
        public userService: UsersService,
        private fb: UntypedFormBuilder,
        private organisationService: OrganisationService,
        private snackBar: MatSnackBar,
        private apiService: ApiService,
        private activatedRoute: ActivatedRoute,
        private settingsService: SettingsService,
    )
    {
        this.title = environment.title;

        this.activatedRoute.data.subscribe({
            next: response => {
                let responseData = response.organisation.data;

                this.organisationName = responseData.name ?? '';

                this.organisationForm = new FormGroup({
                    trading_address: new FormGroup({
                        address_id: new FormControl(responseData.trading_address?.id ?? null),
                        type_id: new FormControl(AddressType.TradingAddress ?? null),
                        title: new FormControl('Trading Address'),
                        address_line_1: new FormControl(responseData.trading_address?.address_line_1 ?? null, Validators.required),
                        address_line_2: new FormControl(responseData.trading_address?.address_line_2 ?? null),
                        address_line_3: new FormControl(responseData.trading_address?.address_line_3 ?? null),
                        locality: new FormControl(responseData.trading_address?.locality ?? null),
                        county: new FormControl(responseData.trading_address?.county ?? null),
                        country: new FormControl(responseData.trading_address?.country ?? null),
                        postcode: new FormControl(responseData.trading_address?.postcode ?? null, Validators.required),
                    }),
                    billing_address: new FormGroup({
                        address_id: new FormControl(responseData.billing_address?.id ?? null),
                        type_id: new FormControl(AddressType.BillingAddress ?? null),
                        title: new FormControl('Billing Address'),
                        address_line_1: new FormControl(responseData.billing_address?.address_line_1 ?? null, Validators.required),
                        address_line_2: new FormControl(responseData.billing_address?.address_line_2 ?? null),
                        address_line_3: new FormControl(responseData.billing_address?.address_line_3 ?? null),
                        locality: new FormControl(responseData.billing_address?.locality ?? null),
                        county: new FormControl(responseData.billing_address?.county ?? null),
                        country: new FormControl(responseData.billing_address?.country ?? null),
                        postcode: new FormControl(responseData.billing_address?.postcode ?? null, Validators.required),
                    }),
                    pricing: new FormGroup({
                        pricing_enabled: new FormControl(this.settingsService.isPricingEnabled()),
                    }),
                });
            },
            error: error => {
                this.apiService.handleError(error);
            }
        });
    }

    public async submitForm()
    {
        if (!this.f.billing_address.valid || !this.f.trading_address.valid) {
            this.snackBar.open('Please fill out the form correctly', 'Ok', {duration: 2500});
            return;
        }

        try {
            const billingAddressResponse = await firstValueFrom(this.organisationService.createAddress(this.f.billing_address.value));
            let tenant = this.authService.tenant;
            tenant.billing_address = billingAddressResponse.data;
            this.authService.tenant = tenant;

            const tradingAddressResponse = await firstValueFrom(this.organisationService.createAddress(this.f.trading_address.value));
            tenant = this.authService.tenant;
            tenant.trading_address = tradingAddressResponse.data;
            this.authService.tenant = tenant;

            const pricingEnabled = !!this.organisationForm.get('pricing.pricing_enabled')?.value;
            await firstValueFrom(this.apiService.updateSettings('settings/pricing', {pricing_enabled: pricingEnabled}));
            this.settingsService.setPricingEnabled(pricingEnabled);

            await this.router.navigateByUrl('/app/sites');
        } catch (error) {
            this.apiService.handleError(error);
            this.snackBar.open('Please fill out the form correctly', 'Ok', {duration: 2500});
        }
    }

    public onFileChange(pFileList: File[])
    {
        this.f.logo.setValue(pFileList[0]);
        let reader = new FileReader();
        reader.onload = (event: any) => {
            this.imagePreview = event.target.result;
        };
        reader.readAsDataURL(pFileList[0]);
    }

    public removeFile()
    {
        this.f.logo.setValue(null);
        this.imagePreview = null;
    }

    public sameAsTradingAddress(event)
    {
        if (event.checked) {
            this.previousBillingAddress = {...this.f.billing_address.value};
            const {address_id, type_id, title, ...addressFields} = this.f.trading_address.value;
            this.f.billing_address.patchValue(addressFields);
        } else if (this.previousBillingAddress) {
            this.f.billing_address.patchValue(this.previousBillingAddress);
            this.previousBillingAddress = null;
        }
    }

    public addSystemOwner(owner_id?)
    {
        const system_owner = this.fb.group({
            id: [owner_id ?? null, Validators.required],
            name: ['beginner', Validators.required]
        });

        this.system_owners.push(system_owner);
    }

    public removeSystemOwner(ownerIndex: number)
    {
        this.system_owners.removeAt(ownerIndex);
    }

    public forceUppercaseConditionally(formGroup, formControlName, event)
    {
        this.organisationForm.get(`${formGroup}.${formControlName}`).setValue(event.target.value.toUpperCase());
    }

    public logout()
    {
        this.authService.logout();
        this.router.navigate(['/login']).then(r => r);
    }
}
